import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  TextField
} from "@mui/material";
import { useSaveSignalUserTeam } from "applications/signal/hooks/gqlHooks";
import { AlertSnackbarProps } from "features/components/alertSnackbar";
import { useAuth } from "hooks/useAuth";
import { setGlobalMessage } from 'hooks/useGlobalMessage';
import { UserTeamUpsertModalSchema, UserTeamUpsertModalData } from "./schema";
import { SignalUserTeam } from "gql/graphql";

type Props = {
  registered: SignalUserTeam | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  setSnackBarMessage: (message: AlertSnackbarProps | null) => void;
}

export default function UserTeamUpsertModal({ registered, open, setOpen, setSnackBarMessage }: Props) {
  const { signalUser } = useAuth();
  const { saveSignalUserTeam } = useSaveSignalUserTeam();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UserTeamUpsertModalData>({
    resolver: zodResolver(UserTeamUpsertModalSchema),
    defaultValues: {
      name: registered?.name || "",
    },
  });
  useEffect(() => {
    reset({
      name: registered?.name || "",
    });
  }, [registered, reset]);

  const processName = registered?.id ? "更新" : "作成";

  const handleClickClose = () => {
    setOpen(false);
  }

  const onSubmit = async (data: UserTeamUpsertModalData) =>  {
    if (!signalUser?.signal_group_id) {
      return;
    }
    const { data: result, error } = await saveSignalUserTeam({data: {
      id: registered?.id,
      name: data.name,
    }});
    if (error) {
      setGlobalMessage({
        message: `チームの${processName}に失敗しました。`,
        severity: "error",
      });
    } else {
      setSnackBarMessage({
        message: `チームを${processName}しました。`,
        severity: "success",
      });
      handleClickClose();
    }
  }

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableRestoreFocus
      onClose={handleClickClose}
    >
      <DialogTitle align="center">
        チーム{processName}
      </DialogTitle>
      <IconButton
        onClick={handleClickClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
        }}
      >
        <CloseIcon />
      </IconButton>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ display: "flex", flexDirection: "row", justifyContent: "center" }}>
          <FormControl
            sx={{ width: "500px" }}
            margin="normal"
            error={!!errors.name}
          >
            <TextField
              {...register("name")}
              variant="standard"
              label="チーム名"
              error={!!errors.name}
              helperText={errors.name?.message}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
          >
            {processName}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
